<template>
  <section class="dropdown-binder" @keyup.esc="toggleDropdown(false)" tabindex="0" @keydown.down="onArrowDown" @keydown.up="onArrowUp" @keyup.enter="handleItemEnter">
    <div class="u-display-flex u-align-items-center">
      <component
        ref="binded-component"
        :is="buttonName"
        v-bind="buttonPropsDecorated"
        @click.native="handleButtonClick"
      />
    </div>
    <!-- Tricky part, we're gonna append this to body level to avoid annoying z-index stuff -->
    <!-- Also, invisible-modal is to prevent scrolling -->
    <div
      ref="invisible-modal"
      :style="{
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        zIndex: this.$arStyle.zIndex.globalHighest,
        display: isOpen ? 'block' : 'none',
      }"
      @click.stop.prevent="handleInvisibleModalClick"
    />
    <div
      ref="dropdown-wrapper"
      :style="{
        display: 'inline-block',
        position: 'fixed',
        top: dialogBoxPosition.top,
        left: dialogBoxPosition.left,
        right: dialogBoxPosition.right,
        bottom: dialogBoxPosition.bottom,
        height: isOpen ? dialogBoxPosition.height : '0',
        opacity: isOpen ? '1' : '0',
        overflow: 'hidden',
        zIndex: this.$arStyle.zIndex.globalHighest + 1,
      }"
    >
      <Dropdown
        ref="dropdown"
        v-model="arrowCounter"
        v-bind="dropdownPropsDecorated"
        :is-open="isOpen"
        :style="dropdownStyle"
        :dropdownWidth="dropdownWidth"
        :divider-style="dividerStyle"
        :classNames="classNames"
        @select="handleItemSelect"
        @tabSelect="handleTabSelect"
        @searchStringChange="handleSearchStringChange"
        @controlClick="handleControlClick"
        @close="toggleDropdown(false)"
        :hasFixedFooter="hasFixedFooter"
        @fixedFooterClick="() => { $emit('fixedFooterClick') }"
      />
    </div>
  </section>
</template>

<script>
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import Dropdown from './Dropdown';

export default {
  name: 'DropdownBinder',
  components: {
    Dropdown,
  },
  props: {
    align: {
      type: String,
      default: 'right'
    },
    classNames: {
      type: Array,
      default: () => [],
    },
    pixelShift: {
      type: Number,
      default: 5,
    },
    verticalPixelShift: {
      type: Number,
      default: 5,
    },
    buttonName: {
      type: String,
      default: 'simple-button',
    },
    buttonProps: {
      type: Object,
      default: () => ({
        text: 'have your props'
      }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dropdownProps: {
      type: Object,
      default: () => ({
        maxHeight: '300px',
        hasFilter: false,
        items: [],
      }),
    },
    hasFixedFooter: {
      type: Boolean,
      default: false,
    },
    dividerStyle: {
      type: Object,
      default: () => {},
    }
  },
  data() {
    return {
      isOpen: false,
      dialogBoxPosition: {},
      lockScrollY: null,
      verticalAlign: 'bottom',
      arrowCounter: -1,
    };
  },
  computed: {
    buttonPropsDecorated() {
      return {
        ...this.buttonProps,
        disabled: this.disabled,
        active: this.isOpen,
      };
    },
    dialogArrowAlign() {
      return `${this.verticalAlign}-${this.align}`;
    },
    dropdownPropsDecorated() {
      return {
        ...this.dropdownProps,
        loading: this.loading,
        arrowAlign: this.dialogArrowAlign,
      }
    },
    dropdownStyle() {
      return {
        ...this.dropdownProps.style,
        margin: this.dialogBoxPosition.padding,
        boxShadow: '1px 2px 8px 1px rgba(0, 0, 0, .07)',
      }
    },
    dropdownWidth() {
      if (!this.dropdownProps.style || !this.dropdownProps.style.width) {
        return null
      }
      return parseInt(this.dropdownProps.style.width.replace("px", ""));
    },
  },
  watch: {
    loading() {
      setTimeout(this.updateDropdownPosition, 30);
    },
    'dropdownProps.items': function(newVal, oldVal) {
      if (newVal.length !== oldVal.length) {
        this.$nextTick(() => {
          this.updateDropdownPosition();
        })
      }
    }
  },
  created() {
    window.addEventListener('resize', this.handleWindowResize);
  },
  mounted() {
    this.attachSelectBoxToBody();
    this.updateDropdownPosition();
  },
  beforeDestroy() {
    this.removeSelectBoxFromBody();
    window.removeEventListener('resize', this.handleWindowResize);
    enableBodyScroll(this.$refs.dropdown.$el);
  },
  methods: {
    handleItemEnter() {
      this.$emit('select', this.dropdownProps.items[this.arrowCounter])
      this.toggleDropdown(false)
    },
    onArrowDown (e) {
      e.preventDefault()
       if (this.arrowCounter === this.dropdownProps.items.length - 1) {
          let idx = this.dropdownProps.items.findIndex((item, index) => {
            return !item.disabled && index !== this.arrowCounter
          })
          this.arrowCounter = idx
      } else {
        let nextArrowIndex = this.dropdownProps.items.findIndex((item, index) => {
          return !item.disabled && index > this.arrowCounter
        })
        if (nextArrowIndex === -1) {
          let idx = this.dropdownProps.items.findIndex((item, index) => {
            return !item.disabled && index !== this.arrowCounter
          })
          this.arrowCounter = idx
          return
        }
        this.arrowCounter = nextArrowIndex
      }
    },
    onArrowUp (e) {
      e.preventDefault()
      let nextNonDisabledIdx
      if (this.arrowCounter === (0 || -1)) {
        nextNonDisabledIdx = this.dropdownProps.items.map((item, index) => {
          return !item.disabled && index !== this.arrowCounter
        }).lastIndexOf(true)
        this.arrowCounter = nextNonDisabledIdx
        return
      }
      nextNonDisabledIdx = this.dropdownProps.items.map((item, index) => {
        return !item.disabled && index < this.arrowCounter
      }).lastIndexOf(true)

      if (nextNonDisabledIdx === -1) {
        nextNonDisabledIdx = this.dropdownProps.items.map((item, index) => {
          return !item.disabled && index !== this.arrowCounter
        }).lastIndexOf(true)
      }

      this.arrowCounter = nextNonDisabledIdx
    },
    // Just attach the dialog to body level, remove it when we don't need it anymore
    attachSelectBoxToBody() {
      document.body.appendChild(this.$refs['invisible-modal']);
      document.body.appendChild(this.$refs['dropdown-wrapper']);
    },
    removeSelectBoxFromBody() {
      document.body.removeChild(this.$refs['invisible-modal']);
      document.body.removeChild(this.$refs['dropdown-wrapper']);
    },
    getDropdownElem() {
      return this.$refs['dropdown'];
    },
    updateDropdownPosition() {
      if (!this.getDropdownElem()) {
        return;
      }
      const DropdownHt = this.getDropdownElem().$el.offsetHeight;
      const elementWt = this.$el.offsetWidth;
      const elementHt = this.$el.offsetHeight;
      const { top, left, right, bottom } = this.$el.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      let fixedLeft;
      let fixedRight;
      const padding = 5;

      if (this.align === 'left') {
        fixedRight = `${windowWidth - right - padding - this.pixelShift}px`;
      } else if (this.align === 'center') {
        fixedLeft = `${left - padding - this.pixelShift + (elementWt/2) - (this.dropdownWidth/2)}px`;
      } else {
        fixedLeft = `${left - padding - this.pixelShift}px`;
      }
      if (top + elementHt + DropdownHt + 10 > windowHeight) {
        this.verticalAlign = 'top';
        this.dialogBoxPosition = {
          bottom: `${windowHeight - top + this.verticalPixelShift}px`,
          left: fixedLeft,
          right: fixedRight,
          height: `${DropdownHt + padding * 2}px`,
          padding: `${padding}px`,
        };
      } else {
        this.verticalAlign = 'bottom';
        this.dialogBoxPosition = {
          top: `${top + elementHt + this.verticalPixelShift}px`,
          left: fixedLeft,
          right: fixedRight,
          height: `${DropdownHt + padding * 2}px`,
          padding: `${padding}px`,
        };
      }
    },
    toggleDropdown(val) {
      this.isOpen = val;
      if (val) {
        this.updateDropdownPosition();

        // Lock scroll
        this.lockScrollY = window.scrollY;
        disableBodyScroll(this.$refs.dropdown.$el);

        this.getDropdownElem().resetScroll();
      } else {
        // Reset filterString in case it exists
        this.getDropdownElem().filterString = '';
        enableBodyScroll(this.$refs.dropdown.$el);
      }

      this.$emit('toggle', val);
      this.arrowCounter = -1
    },
    handleButtonClick(e) {
      // That is the end of the click, we don't want to propagate it to parents
      e.stopPropagation();
      this.$emit('button-click', e)
      if (this.disabled) {
        return;
      }
      this.toggleDropdown(!this.isOpen);
    },
    handleTabSelect(tab) {
      this.$emit('tabSelect', tab);
      setTimeout(this.updateDropdownPosition, 30);
    },
    handleItemSelect(item) {
      this.$emit('select', item);

      if (item?.closeOnClick === false) {
        return;
      }

      this.toggleDropdown(false);
    },
    handleControlClick(item, control) {
      this.$emit('controlClick', item, control);
    },
    handleSearchStringChange(val) {
      this.$emit('searchStringChange', val);
      setTimeout(this.updateDropdownPosition, 30);
    },
    handleInvisibleModalClick(event) {
      this.toggleDropdown(false);
    },
    handleWindowResize() {
      this.updateDropdownPosition();
    },
    handleScroll(event) {
      event.preventDefault();
      window.scrollTo(window.scrollX, this.lockScrollY);
      event.stopPropagation();
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-binder {
  position: relative;
  display: inline-block;
  outline: none;
}
</style>
